import React, {useState, useEffect} from 'react';
import moment from 'moment';

export default function SummarySectionLineItem({label, value, type, showEdit, hasVisitedPhase, edit, emptyStateString, disableEmptyStateAction, detectCapitalizationIssues}){

	let [displayVal, setDisplayVal] = useState("");

	let canUseEmptyStateAction = showEdit && !disableEmptyStateAction;

	let formatCommaSeparatedText = (stringValue, requireAlphabeticSegments = false) => {
		if (typeof stringValue !== "string" || stringValue.indexOf(",") === -1){
			return stringValue;
		}

		let segments = stringValue.split(",");
		let hasMultipleSegments = segments.length > 1;
		let hasEmptySegment = segments.some((segment) => segment.trim().length === 0);

		if (!hasMultipleSegments || hasEmptySegment){
			return stringValue;
		}

		if (requireAlphabeticSegments){
			let eachSegmentContainsLetters = segments.every((segment) => /[A-Za-z]/.test(segment));
			if (!eachSegmentContainsLetters){
				return stringValue;
			}
		}

		return segments.map((segment) => segment.trim()).join(", ");
	}

	// Formats a value as a North American phone number if (and only if) it can be
	// confidently normalized to exactly 10 digits. Otherwise the original value is
	// returned untouched so we never guess at malformed/partial input.
	let formatPhoneNumber = (rawValue) => {
		if (typeof rawValue !== "string") return rawValue;

		// Strip everything that isn't a digit (spaces, dashes, parens, "+", letters, etc.)
		let digitsOnly = rawValue.replace(/\D/g, "");

		// A leading "1" is the US/Canada country code (e.g. "+1 (123) 456-7890" -> 11 digits).
		// Only strip it when it results in a clean 10-digit number.
		if (digitsOnly.length === 11 && digitsOnly.charAt(0) === "1"){
			digitsOnly = digitsOnly.substring(1);
		}

		// Anything other than exactly 10 digits (too few, too many, or non-numeric
		// characters mixed in) can't be safely formatted, so return the original value.
		if (digitsOnly.length !== 10) return rawValue;

		let areaCode = digitsOnly.substring(0, 3);
		let prefix = digitsOnly.substring(3, 6);
		let lineNumber = digitsOnly.substring(6, 10);

		return `(${areaCode}) ${prefix}-${lineNumber}`;
	}

	let shouldSuggestCapitalizationEdit = (rawValue) => {
		if (typeof rawValue !== "string") return false;

		let trimmedValue = rawValue.trim();
		if (!trimmedValue) return false;

		let lowercaseParticles = [
			"a", "an", "and", "as", "at", "by",
			"da", "de", "del", "della", "der", "di", "dos", "du",
			"el", "la", "las", "le", "los",
			"in", "of", "on", "or", "the", "to", "van", "von", "with", "without"
		];

		let normalizeWord = (word) => word.replace(/^[^A-Za-z]+|[^A-Za-z]+$/g, "");
		let stripJoiners = (word) => word.replace(/['’.-]/g, "");

		let isAllowedMcMacMak = (word) => {
			let compactWord = stripJoiners(word);
			return /^(Mc|Mac|Mak)[A-Z][a-z]+$/.test(compactWord);
		};

		let isTitleLikeWord = (word) => {
			let parts = word.split(/['’.-]/).filter((part) => part.length > 0);
			if (parts.length === 0) return false;

			return parts.every((part) => {
				if (!/[A-Za-z]/.test(part)) return true;
				if (part.length === 1) return /^[A-Z]$/.test(part);
				return /^[A-Z][a-z]+$/.test(part);
			});
		};

		let segments = trimmedValue
			.split(",")
			.map((segment) => segment.trim())
			.filter((segment) => segment.length > 0);

		if (segments.length === 0) return false;

		return segments.some((segment) => {
			let segmentLetters = segment.replace(/[^A-Za-z]/g, "");
			if (segmentLetters.length >= 4){
				if (segmentLetters === segmentLetters.toLowerCase()) return true;
				if (segmentLetters === segmentLetters.toUpperCase()) return true;
			}

			let words = segment
				.split(/\s+/)
				.map(normalizeWord)
				.filter((word) => word.length > 0);

			return words.some((word) => {
				let compactWord = stripJoiners(word);
				if (!/[A-Za-z]/.test(compactWord)) return false;

				let normalizedWord = compactWord.toLowerCase();
				if (lowercaseParticles.indexOf(normalizedWord) !== -1) return false;
				if (isAllowedMcMacMak(word)) return false;
				if (isTitleLikeWord(word)) return false;

				if (/^[A-Z]{1,3}$/.test(compactWord)) return false;
				if (/^[a-z]+$/.test(compactWord)) return true;
				if (/^[A-Z]+$/.test(compactWord)) return compactWord.length >= 4;

				return true;
			});
		});
	}

	let showCapitalizationHint = detectCapitalizationIssues && shouldSuggestCapitalizationEdit(value);

	let emptyState = canUseEmptyStateAction ? <>
		<span
			className="text-light"
			role="button"
			tabIndex={0}
			onClick={edit}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") edit();
			}}
			style={{cursor: "pointer"}}
		>
			{emptyStateString}
		</span>
		{hasVisitedPhase ? 
			<button
				type="button"
				className="btn btn-xs btn-round btn-warning fs-12 ml-3 px-3 py-0"
				onClick={edit}
			>
				Fill In
			</button>
		: null}
	</> : <span className="text-light">{emptyStateString}</span>

	let calcDisplayVal = (value, type) => {

		if (!value){
			return emptyState;
		} else if (Array.isArray(value)){
			let newDisplayVal = [];
			for (var i = 0; i < value.length; i++){
				let calcedVal = calcDisplayVal(value[i], type[i]);
				if (calcedVal != emptyState){
					newDisplayVal.push(calcedVal);
				}
			}

			if (newDisplayVal.length === 0) newDisplayVal = emptyState;

			return newDisplayVal;
		} else if (type == "checkbox"){
			if (value.length == 0) return emptyState;
			else return formatCommaSeparatedText(value);
		} else if (type == "date"){
			if (typeof value !== "object"){
				return emptyState;
			} else {
				return (moment(value).format("MM-DD-YYYY"))
			}
		} else if (type == "phone"){
			return formatPhoneNumber(value)
		} else if (type == "time"){
			return moment(value).format("hh:mm a") + ""
		} else if (type == "datetime"){
			return moment(value).format("MM-DD-YYYY hh:mm a") + ""
		} else if (typeof value == "string"){
			return formatCommaSeparatedText(value, true);
		} else if (typeof value == "Object"){
			console.error("Uncaught Object in SummarySectionLineItem:", value)
			return "Error: uncaught object";
		} else {
			return value;
		}

	}

	useEffect(() => {
		setDisplayVal(calcDisplayVal(value, type));
	}, [value, type, showEdit, hasVisitedPhase, emptyStateString, disableEmptyStateAction])

	let displayParagraph = (displayVal) => {
		return <p class="strong fs-16 p-0 mb-4 lh-1">{displayVal}</p>
	}

	let renderDisplayVal = (displayVal) => {

		if (Array.isArray(displayVal)) {

			return displayVal.map((displayVal, key) => {
				if (key > 0 && displayVal == emptyState) return null;
				else return displayParagraph(displayVal);
			})
		} else {
			return displayParagraph(displayVal)
		}
			
	}

	return(<div class="col-12 float-left">
		<div class="col-11 p-0 m-0 float-left">
			{label !== null && label !== undefined ? <label class="text-light">{label}</label> : null}
			{renderDisplayVal(displayVal)}
			{showCapitalizationHint ?
				<p class="small text-warning mb-4 lh-1">
					<i class="fa fa-info-circle mr-1" aria-hidden="true"></i>
					Please review capitalization.
					{canUseEmptyStateAction ?
						<span
							className="strong ml-2"
							role="button"
							tabIndex={0}
							onClick={edit}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") edit();
							}}
							style={{cursor: "pointer"}}
						>
							Edit
						</span>
					: null}
				</p>
			: null}
		</div>
	</div>)

}